import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import CompanyDashboard from './CompanyDashboard';
import ApplicationsManagement from './ApplicationsManagement';
import SubcontractorDashboard from './SubcontractorDashboard';
import SubcontractorProfile from './SubcontractorProfile';
import SuperAdminDashboard from './SuperAdminDashboard';
import SubcontractorInviteOnboarding from './SubcontractorInviteOnboarding';
import { requiresSubcontractorInviteWizard as userRequiresSubcontractorInviteWizard } from '../utils/subcontractorInviteOnboardingGate';
import { employerHasPermission } from '../utils/userDashboardUi';

const UnifiedRoutes = ({ user, onLogout, onUserRefresh }) => {
  const location = useLocation();

  // Check if user is super admin
  const isSuperAdmin = user && (user.role === 'superuser' || user.role === 'super_admin');
  
  // If super admin, always show super admin dashboard
  if (isSuperAdmin) {
    return <SuperAdminDashboard user={user} onLogout={onLogout} />;
  }
  
  if (!user) {
    return <Navigate to="/unauthorized" replace />;
  }

  const canUseSubcontractorDashboard = Array.isArray(user?.employers) && user.employers.length > 0;

  const requiresSubcontractorInviteWizard = () => userRequiresSubcontractorInviteWizard(user);

  const defaultDashboardShell = () => {
    if (!canUseSubcontractorDashboard) return 'employer';
    const employerCount = Array.isArray(user?.employers) ? user.employers.length : 0;
    const subcontractorCount = Array.isArray(user?.subcontractors) ? user.subcontractors.length : 0;

    if (employerCount > 0 || subcontractorCount > 0) {
      if (employerCount >= subcontractorCount) return 'subcontractor';
      if (subcontractorCount > employerCount) return 'employer';
    }

    const regType = String(user?.registration_type || '').toLowerCase();
    if (regType === 'invite') return 'subcontractor';
    if (regType === 'reg_employer') return 'employer';
    return 'employer';
  };

  const renderComponent = () => {
    const pathRaw = location.pathname || '/';
    const path = pathRaw.replace(/\/$/, '') || '/';
    const shell = defaultDashboardShell();
    const menuParam = new URLSearchParams(location.search).get('menu');

    if (
      menuParam?.toLowerCase() === 'users' &&
      employerHasPermission(user, 'view_users') &&
      (path === '/dashboard/employer' || (path === '/dashboard' && shell === 'employer'))
    ) {
      return <Navigate to="/profile/users" replace />;
    }

    if (requiresSubcontractorInviteWizard()) {
      const isProfilePath =
        path === '/profile' ||
        path === '/profile/organization' ||
        path === '/profile/users';
      const isSubcontractorDestination =
        path === '/dashboard/subcontractor' ||
        path === '/application' ||
        isProfilePath ||
        ((path === '/dashboard' || path === '/dashboard/') && shell === 'subcontractor');
      if (isSubcontractorDestination) {
        return <SubcontractorInviteOnboarding onCompleted={onUserRefresh} />;
      }
    }
    if (
      path === '/profile' ||
      path === '/profile/organization' ||
      path === '/profile/users'
    ) {
      return <SubcontractorProfile user={user} />;
    }
    if (path === '/dashboard/employer' || path === '/subcontractors' || path === '/reports' || path === '/settings') {
      return <CompanyDashboard user={user} onLogout={onLogout} />;
    }
    if (path === '/dashboard/subcontractor' || path === '/application') {
      if (!canUseSubcontractorDashboard) {
        return <Navigate to="/dashboard/employer" replace />;
      }
      return <SubcontractorDashboard user={user} onLogout={onLogout} />;
    }
    if (path === '/applications' || path === '/applications/') {
      return <ApplicationsManagement user={user} />;
    }
    if (path === '/dashboard' || path === '/dashboard/') {
      return defaultDashboardShell() === 'employer'
        ? <CompanyDashboard user={user} onLogout={onLogout} />
        : <SubcontractorDashboard user={user} onLogout={onLogout} />;
    }
    return defaultDashboardShell() === 'employer'
      ? <CompanyDashboard user={user} onLogout={onLogout} />
      : <SubcontractorDashboard user={user} onLogout={onLogout} />;
  };

  return renderComponent();
};

export default UnifiedRoutes;
